import * as React from "react";
import { useMutation, useQuery } from "react-query";
import { SystemSettingsService, AppBundle, AppBundleApplication } from "gen/clients";
import { Drawer, Stack } from "@mui/material";
import { DrawerHeader } from "components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "components/DrawerBody/DrawerBody";
import { DrawerActions } from "components/DrawerActions/DrawerActions";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { Form, Formik } from "formik";
import { ApplicationSelector } from "./ApplicationSelector";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import * as Yup from "yup";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
  existingAppBundles: AppBundle[];
}

enum FieldName {
  id = "id",
  name = "name",
  applications = "applications"
}

interface FormValues {
  [FieldName.id]: string;
  [FieldName.name]: string;
  [FieldName.applications]: AppBundleApplication[];
}

const CreateAppBundlePanel: React.FC<Props> = ({ onClose, onSuccess, existingAppBundles }) => {
  const { data: systemSettings, isLoading: isLoadingSettings } = useQuery(["getSystemSettings"], {
    queryFn: SystemSettingsService.getSystemSettings
  });

  const {
    mutate,
    isLoading: isCreating,
    error
  } = useMutation(SystemSettingsService.updateSystemSettings, {
    onSuccess
  });

  const existingIds = React.useMemo(
    () => new Set(existingAppBundles.map(bundle => bundle.id.toLowerCase())),
    [existingAppBundles]
  );

  const existingNames = React.useMemo(
    () => new Set(existingAppBundles.map(bundle => bundle.name.toLowerCase())),
    [existingAppBundles]
  );

  const validationSchema = Yup.object().shape({
    [FieldName.id]: Yup.string()
      .required("ID is required")
      .matches(/^[a-zA-Z0-9_-]+$/, "ID can only contain letters, numbers, hyphens, and underscores")
      .test("unique-id", "ID must be unique", value => !value || !existingIds.has(value.toLowerCase())),
    [FieldName.name]: Yup.string()
      .required("Name is required")
      .test("unique-name", "Name must be unique", value => !value || !existingNames.has(value.toLowerCase())),
    [FieldName.applications]: Yup.array().min(0, "At least one application must be selected")
  });

  const initialValues: FormValues = {
    [FieldName.id]: "",
    [FieldName.name]: "",
    [FieldName.applications]: []
  };

  const handleSubmit = React.useCallback(
    (values: FormValues) => {
      if (!systemSettings) {
        return;
      }

      const newAppBundle: AppBundle = {
        id: values.id,
        name: values.name,
        isDisabled: false,
        applications: values.applications
      };

      const updatedAppBundles = [...(systemSettings.appBundles || []), newAppBundle];

      mutate({
        requestBody: {
          ...systemSettings,
          appBundles: updatedAppBundles
        }
      });
    },
    [systemSettings, mutate]
  );

  if (isLoadingSettings) {
    return (
      <Drawer open={true} onClose={onClose} anchor="right" PaperProps={{ sx: { width: 600 } }}>
        <DrawerHeader title="Create App Bundle" onClose={onClose} />
        <DrawerBody isLoading={true}>
          <div />
        </DrawerBody>
      </Drawer>
    );
  }

  return (
    <Drawer open={true} onClose={onClose} anchor="right" PaperProps={{ sx: { width: 600 } }}>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ isSubmitting, isValid, dirty, values, setFieldValue }) => (
          <Form>
            <DrawerHeader title="Create App Bundle" onClose={onClose} />
            <DrawerBody>
              <Stack spacing={3}>
                <TextInputField
                  name={FieldName.id}
                  label="Unique ID"
                  required
                  helperText="Must be unique across all app bundles. Use letters, numbers, hyphens, and underscores only."
                  autoFocus
                />

                <TextInputField
                  name={FieldName.name}
                  label="Unique Name"
                  required
                  helperText="Display name for this app bundle. Must be unique."
                />

                <ApplicationSelector
                  applications={values.applications}
                  onChange={applications => setFieldValue(FieldName.applications, applications)}
                  disabled={isSubmitting}
                />

                {error && <ApiErrorMessage apiError={error} />}
              </Stack>
            </DrawerBody>
            <DrawerActions>
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                isLoading={isSubmitting || isCreating}
                disabled={!isValid || !dirty}
              >
                Create App Bundle
              </LoadingButton>
            </DrawerActions>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};

export { CreateAppBundlePanel };
